import {
  APPLICATION_SCHEMA_VERSION,
  isStoredRecordType,
  validateStoredRecordData,
  type StoredRecordDataMap,
  type StoredRecordType,
} from "./schemas";
import {
  isRecord,
  validationFailure,
  validationSuccess,
  type ValidationResult,
} from "./validation";

export interface StorageEnvelope<
  K extends StoredRecordType = StoredRecordType,
> {
  readonly schemaVersion: 1;
  readonly data: StoredRecordDataMap[K];
  readonly key: string;
  readonly recordType: K;
  readonly savedAt: string;
}

export function createStorageEnvelope<K extends StoredRecordType>(
  recordType: K,
  key: string,
  savedAt: string,
  data: StoredRecordDataMap[K],
): StorageEnvelope<K> {
  return {
    schemaVersion: APPLICATION_SCHEMA_VERSION,
    recordType,
    key,
    savedAt,
    data,
  };
}

export function validateStorageEnvelope(
  value: unknown,
  source: string,
): ValidationResult<StorageEnvelope> {
  if (!isRecord(value)) {
    return validationFailure(source, "$", "expected a JSON object");
  }
  if (value.schemaVersion !== APPLICATION_SCHEMA_VERSION) {
    return validationFailure(
      source,
      "$.schemaVersion",
      "expected application schemaVersion 1",
    );
  }
  if (!isStoredRecordType(value.recordType)) {
    return validationFailure(source, "$.recordType", "unknown record type");
  }
  if (typeof value.key !== "string") {
    return validationFailure(source, "$.key", "expected a string");
  }
  if (typeof value.savedAt !== "string") {
    return validationFailure(source, "$.savedAt", "expected a string");
  }
  const data = validateStoredRecordData(value.recordType, value.data, source);
  if (!data.ok) {
    return validationFailure(
      data.error.source,
      `$.data${data.error.path.slice(1)}`,
      data.error.problem,
    );
  }
  return validationSuccess(value as unknown as StorageEnvelope);
}
